    <script setup lang="ts">
// Define the component name to fix the linter error
defineOptions({
  name: 'DiplomacySection'
})

import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Define type for tab
interface DiplomacyTab {
  name: string
  target: string
  activeKey: string
  pill: string
  joinRequests?: number
}

const playerRelations = usePlayerRelationStore()
const gameStore = useGameStore()
const allianceStore = useAllianceStore()

const pills = computed(() => {
  return playerRelations.getAllRelationsWithPendingActions()
})

// Get the player's alliance name
const playerAllianceName = computed(() => {
  if (gameStore.player?.allianceId) {
    const alliance = allianceStore.alliances.get(gameStore.player.allianceId)
    // Skip if alliance is deleted
    if (alliance?.deleted) {
      return null
    }
    return alliance?.name || 'My Alliance'
  }
  return null
})

const activeAlliances = computed(() => {
  return Array.from(allianceStore.alliances.values()).filter(alliance => !alliance.deleted)
})

const activeAlliancesCount = computed(() => {
  return activeAlliances.value.length
})

// Get the count of join requests for the player's alliance
const joinRequestsCount = computed(() => {
  if (!gameStore.player?.allianceId) {
    return 0
  }
  const alliance = allianceStore.alliances.get(gameStore.player.allianceId)
  // Skip if alliance is deleted
  if (alliance?.deleted) {
    return 0
  }
  
  // Check if player is alliance leader, as only leaders can see join requests
  const isLeader = alliance?.leaderPlayerId === gameStore.player?.id

  if (!alliance || !alliance.joinRequests || !isLeader) {
    return 0
  }
  return alliance.joinRequests.length
})

const tabs = computed(() => {
  const baseTabs: DiplomacyTab[] = [
    {
      name: t('diplomacy.tabs.overview'),
      target: '/diplomacy/overview',
      activeKey: 'DiplomacyOverview',
      pill: 'overview'
    },
    {
      name: t('diplomacy.tabs.spies'),
      target: '/diplomacy/spies',
      activeKey: 'DiplomacySpies',
      pill: 'spies'
    },
    {
      name: t('diplomacy.tabs.alliances'),
      target: '/diplomacy/alliances',
      activeKey: 'DiplomacyAlliances',
      pill: 'alliance'
    }
  ]

  // Add My Alliance tab only if player is in an alliance
  if (playerAllianceName.value) {
    baseTabs.push({
      name: playerAllianceName.value,
      target: '/diplomacy/alliance/',
      activeKey: 'Alliance',
      pill: 'myalliance',
      joinRequests: joinRequestsCount.value
    })
  }

  return baseTabs
})
</script>

<template>
  <div class="text-sm font-medium text-center border-b text-gray-400 border-gray-700">
    <div class="overflow-x-auto whitespace-nowrap no-scrollbar">
      <ul class="flex space-x-2">
        <li v-for="tab in tabs" :key="tab.name">
          <router-link
            :to="tab.target"
            :class="{
              'active text-blue-500 border-blue-500':
                typeof $route.name === 'string' && $route.name.startsWith(tab.activeKey),
              'text-gray-500 border-transparent':
                typeof $route.name !== 'string' || !$route.name.startsWith(tab.activeKey),
              'inline-block p-4 border-b-2 rounded-t-lg hover:border-gray-300 hover:text-gray-300': true
            }"
          >
            {{ tab.name }}
            <span v-if="activeAlliancesCount > 0 && tab.pill === 'alliance'" class="text-white">
              ({{ activeAlliancesCount }})
            </span>
            <span
              v-if="pills.has(tab.pill)"
              class="rounded bg-red-500 text-white text-xs px-1 ml-1"
            >
              {{ pills.get(tab.pill) }}
            </span>
            <!-- Show join requests pill -->
            <span
              v-if="tab.joinRequests && tab.joinRequests > 0"
              class="rounded bg-red-500 text-white text-xs px-1 ml-1"
            >
              {{ tab.joinRequests }}
            </span>
          </router-link>
        </li>
      </ul>
    </div>
  </div>
  <router-view></router-view>
</template>

<style scoped lang="scss"></style>
